<template>
  <div class="info-card rating-card" :id="topic.id" ref="elRef">
    <el-collapse-item :name="topic.id">
      <!-- Title -->
      <template #title>
        <span :aria-label="translation.title" class="title-container">
          <span class="title-text">
            {{ translation.title }}
            <!--<span class="title-border"></span>-->
          </span>
        </span>
      </template>

      <!-- Media Thumbnail -->
      <img
        v-if="topic.photo"
        :src="topic.photo"
        :alt="topic.photo_name || topic.photo"
        class="info-card__media"
      />

      <div
        class="info-card__media info-card__media--video"
        v-if="topic.video"
        v-html="topic.video"
      ></div>

      <!-- Content -->
      <div class="info-card__content" v-if="!data.hasResponseBeenSubmitted">
        <div
          v-if="translation.description"
          v-html="translation.description"
          class="info-card__description"
        ></div>

        <!-- Email Capture Topic -->
        <template v-if="isEmailCaptureTopic">
          <el-input
            type="text"
            :placeholder="helperStore.i18n('rating_ph_first_name') + ' *'"
            v-model="data.form.ratingGuestFirstName"
            maxlength="50"
          />

          <el-input
            type="text"
            :placeholder="helperStore.i18n('rating_ph_last_name') + ' *'"
            v-model="data.form.ratingGuestLastName"
            maxlength="50"
          />

          <el-input
            type="text"
            :placeholder="emailPlaceholder"
            v-model="data.form.ratingGuestEmail"
            maxlength="50"
          />

          <!-- Marketing checkbox -->
          <el-checkbox v-if="topic.is_for_marketing" v-model="data.form.guestMarketingPermission">
            {{ helperStore.i18n('rating_marketing_optin_text') }}

            <a @click.prevent.stop="openPolicyPage" href="#">
              {{ helperStore.i18n('rating_privacy_policy_link') }}
            </a>
          </el-checkbox>

          <!-- Submit button-->
          <a
            @click.prevent="submitFeedback"
            class="btn btn--rating btn--submit"
            :class="{ 'btn--loading': data.isLoading }"
            href="#"
          >
            {{ helperStore.i18n('rating_submit_button') }}
          </a>
        </template>

        <!-- Rating Topic -->
        <template v-if="isRatingTopic">
          <!-- Leave Rating Button -->
          <a
            v-show="!data.isCheckInFormVisible"
            @click.prevent="data.isCheckInFormVisible = true"
            href="#"
            class="btn btn--rating"
          >
            {{ translation.rating_button_title }}
          </a>

          <div v-show="data.isCheckInFormVisible" class="feedback-form">
            <p>{{ translation.rating_star_title }} *</p>
            <el-rate v-model="data.form.ratingValue" size="large" />

            <p>{{ helperStore.i18n('rating_comment_label') }}</p>
            <el-input
              v-model="data.form.ratingGuestComment"
              type="textarea"
              rows="5"
              resize="none"
              maxlength="300"
              show-word-limit
            />

            <p>{{ helperStore.i18n('rating_personal_info_label') }}</p>
            <el-input
              type="text"
              :placeholder="helperStore.i18n('rating_ph_first_name') + ' *'"
              v-model="data.form.ratingGuestFirstName"
              maxlength="50"
            />

            <el-input
              type="text"
              :placeholder="helperStore.i18n('rating_ph_last_name') + ' *'"
              v-model="data.form.ratingGuestLastName"
              maxlength="50"
            />

            <el-input
              type="text"
              :placeholder="phonePlaceholder"
              v-model="data.form.ratingGuestPhone"
              maxlength="50"
            />

            <el-input
              type="text"
              :placeholder="emailPlaceholder"
              v-model="data.form.ratingGuestEmail"
              maxlength="50"
            />

            <!-- Marketing checkbox -->
            <el-checkbox v-if="topic.is_for_marketing" v-model="data.form.guestMarketingPermission">
              {{ helperStore.i18n('rating_marketing_optin_text') }}

              <a @click.prevent.stop="openPolicyPage" href="#">
                {{ helperStore.i18n('rating_privacy_policy_link') }}
              </a>
            </el-checkbox>

            <!-- Submit button-->
            <a
              @click.prevent="submitFeedback"
              class="btn btn--rating btn--submit"
              :class="{ 'btn--loading': data.isLoading }"
              href="#"
            >
              {{ helperStore.i18n('rating_submit_button') }}
            </a>
          </div>
        </template>

        <el-row justify="end">
          <div
            class="info-card__btn"
            @click="onShareClick"
            :style="{ backgroundColor: helperStore.styleData.accentColor }"
          >
            <i class="icon-Share-3"></i>
          </div>
        </el-row>
      </div>

      <div v-if="data.hasResponseBeenSubmitted" class="response-message">
        {{ helperStore.i18n('rating_response_submitted_msg') }}
      </div>
    </el-collapse-item>
  </div>
</template>

<script lang="ts" setup>
import { ShareTypesEnum, Topic, TopicContentTypeEnum } from '@/interfaces/guide.interface'
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import { ElNotification } from 'element-plus'
import api from '@/api'
import snakecaseKeys from 'snakecase-keys'
import { useHelperStore } from '@/pinia/helper.store'

const helperStore = useHelperStore()

const TS_CHECK_IN_SUBMITTED = 'TS_CHECK_IN_SUBMITTED_'
const elRef = ref<HTMLDivElement>(null)
const props = defineProps<{ topic: Topic }>()
const emit = defineEmits<{
  (e: 'show-share-overlay', payload: { type: ShareTypesEnum; topicId: number }): void
}>()
const data = reactive({
  isCheckInFormVisible: false,
  isLoading: false,
  hasResponseBeenSubmitted: false,
  form: {
    ratingGuestFirstName: '',
    ratingGuestLastName: '',
    ratingGuestEmail: '',
    ratingGuestPhone: '',
    ratingGuestComment: '',
    ratingValue: 0,
    guestMarketingPermission: false
  }
})

const translation = computed(() => props.topic.translations[helperStore.currentLanguage])
const phonePlaceholder = computed(
  () => helperStore.i18n('rating_ph_phone_number') + (props.topic.is_phone_required ? ' *' : '')
)
const emailPlaceholder = computed(
  () => helperStore.i18n('rating_ph_email_address') + (props.topic.is_email_required ? ' *' : '')
)
const isRatingTopic = computed(
  () =>
    props.topic.dynamic_content_type === TopicContentTypeEnum.RATING ||
    props.topic.dynamic_content_type === TopicContentTypeEnum.FIRST_IMPRESSIONS
)
const isEmailCaptureTopic = computed(
  () => props.topic.dynamic_content_type === TopicContentTypeEnum.EMAIL_CAPTURE
)

onMounted(() => {
  if (localStorage.getItem(TS_CHECK_IN_SUBMITTED.concat(props.topic.id.toString()))) {
    data.hasResponseBeenSubmitted = true
  }
})

async function submitFeedback() {
  if (
    isRatingTopic.value &&
    !data.form.ratingValue &&
    props.topic.dynamic_content_type === TopicContentTypeEnum.RATING
  ) {
    ElNotification.error('Rating is required')
    return
  }

  if (!data.form.ratingGuestFirstName) {
    ElNotification.error('First name is required')
    return
  }

  if (!data.form.ratingGuestLastName) {
    ElNotification.error('Last name is required')
    return
  }

  // For Email Capture phone is never required
  if (props.topic.is_phone_required && !data.form.ratingGuestPhone) {
    ElNotification.error('Phone number is required')
    return
  }

  // For Email Capture email is always required
  if (props.topic.is_email_required && !data.form.ratingGuestEmail) {
    ElNotification.error('Email is required')
    return
  }

  data.isLoading = true

  try {
    const payload = { ...data.form }

    if (isEmailCaptureTopic.value) {
      delete payload.ratingValue
      delete payload.ratingGuestComment
      delete payload.ratingGuestPhone
    }

    await api.submitCheckIn(props.topic.id, snakecaseKeys(payload))

    data.hasResponseBeenSubmitted = true
    localStorage.setItem(TS_CHECK_IN_SUBMITTED.concat(props.topic.id.toString()), 'true')

    await nextTick(() => {
      const responseDiv = document.querySelector('.response-message')

      responseDiv.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    })
  } finally {
    data.isLoading = false
  }
}

function openPolicyPage() {
  window.open(translation.value.privacy_policy_link, '_blank')
}

function onShareClick() {
  emit('show-share-overlay', { type: ShareTypesEnum.TOPIC, topicId: props.topic.id })
}
</script>

<style lang="scss" scoped>
.title-border {
  position: absolute;
  width: 100%;
  left: 0;
  height: 1px;
  top: 100%;
  background-color: var(--el-color-primary);
}

.feedback-form {
  padding-right: 8px;

  p {
    margin: 10px 0;
  }
}

.btn--rating {
  border-radius: 25px;
  text-shadow: $text-shadow--white;
  font-family: v-bind('helperStore.headerFont');
  font-size: 1rem;
  display: flex;
  margin: 0 auto;
  width: fit-content;
  background-color: var(--el-color-primary);

  &.btn--submit {
    pointer-events: all;
    opacity: 1;
    margin-top: 20px;
    border-radius: $border-radius;
    transition: opacity 0.5s ease-in-out;

    &.btn--loading {
      opacity: 0.3;
      pointer-events: none;
    }
  }
}

:deep(.el-input__wrapper) {
  box-shadow: none;
  border: 1px solid $border-color;
  height: 50px;
  margin: 10px 0;
  transition: border 0.1s ease-in-out;
  border-radius: $border-radius;

  &.is-focus {
    border-color: var(--el-color-primary);
  }

  input {
    font-family: v-bind('helperStore.textFont');
    font-size: 1rem;

    &::placeholder {
      color: $dark-gray;
    }
  }
}

:deep(.el-textarea__inner) {
  border: 1px solid $border-color;
  margin: 10px 0;
  transition: border 0.1s ease-in-out;
  box-shadow: none;
  border-radius: $border-radius;
}

:deep(.el-input__count) {
  bottom: 12px;
}

:deep(.el-checkbox) {
  white-space: normal;
  padding-top: 15px;

  .el-checkbox__label {
    line-height: 1.5;

    a {
      color: var(--el-color-primary);
    }
  }
}

:deep(.el-rate) {
  margin-left: -5px;
  display: flex;

  .el-rate__item {
    @include mediaMobile {
      flex: 1 0 auto;
    }

    @include mediaDesktop {
      padding-right: 30px;
    }
  }

  svg {
    height: 50px;
    width: 50px;
    color: var(--el-color-primary);
  }
}

.response-message {
  border: 2px solid var(--el-color-primary);
  border-radius: $border-radius;
  padding: 10px;
  margin: 14px;
  text-align: center;
  font-size: 1rem;
}

.title-text {
  color: inherit;
  font-family: v-bind('helperStore.headerFont');
}
</style>
